/**
 * AdminAbuse — Phase B MTK abuse dashboard.
 *
 * Left: category-filtered top-scored user list, sorted by dating_abuse_score.
 * Right (drill-down): per-user score breakdown, pattern evidence, recent
 * events, action log, and action buttons. Actions are always logged; in
 * monitor mode they only write to `abuse_actions` without enforcing.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Loader2, RefreshCw, Shield, Ban, Pause, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import BackNavButton from '@/components/BackNavButton';

const CATEGORIES = [
  ['',           'All'],
  ['severe',     'Severe'],
  ['high',       'High'],
  ['suspicious', 'Suspicious'],
  ['elevated',   'Elevated'],
  ['normal',     'Normal'],
];
const CAT_COLOR = {
  severe:     'bg-rose-100 text-rose-800',
  high:       'bg-orange-100 text-orange-800',
  suspicious: 'bg-amber-100 text-amber-800',
  elevated:   'bg-yellow-100 text-yellow-800',
  normal:     'bg-emerald-100 text-emerald-800',
};

function Score({ v }) {
  const n = Number(v || 0);
  const color = n >= 80 ? '#B91C1C' : n >= 60 ? '#EA580C' : n >= 40 ? '#D97706' : n >= 20 ? '#CA8A04' : '#166534';
  return <span className="font-display font-[700] tabular-nums" style={{ color }}>{n.toFixed(1)}</span>;
}

export default function AdminAbuse() {
  const navigate = useNavigate();
  const [ok, setOk] = useState(false);
  const [category, setCategory] = useState('');
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/me').then(() => setOk(true))
      .catch(() => { toast.error('Admin required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/abuse/dashboard?limit=100${category ? `&category=${category}` : ''}`);
      setList(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [category]);

  useEffect(() => { if (ok) load(); }, [ok, load]);

  const openUser = async (uid) => {
    setSelected(uid); setDetail(null);
    try {
      const r = await api.get(`/admin/abuse/user/${uid}`);
      setDetail(r.data);
    } catch (e) { toast.error('Detail failed'); }
  };

  const doAction = async (action) => {
    if (!selected) return;
    const note = ['ban', 'suspend_pending_review'].includes(action)
      ? window.prompt(`Reason (${action}):`, '') || ''
      : '';
    setBusy(true);
    try {
      await api.post(`/admin/abuse/user/${selected}/action`, { action, note });
      toast.success(`Action logged: ${action}`);
      openUser(selected); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  if (!ok) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" /></div>;

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="admin-abuse-page">
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <BackNavButton />
        <h1 className="font-brand text-[26px] font-[600] tracking-tight">Abuse</h1>
        <button onClick={load} disabled={loading} data-testid="abuse-refresh"
          className="ml-auto p-2 rounded-full hover:bg-black/[0.04]"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></button>
      </div>

      <div className="px-5 pt-4">
        <div className="inline-flex gap-1 rounded-full border border-[var(--hairline)] p-1 bg-[var(--surface-2)]">
          {CATEGORIES.map(([k, l]) => (
            <button key={k || 'all'} onClick={() => setCategory(k)}
              data-testid={`abuse-cat-${k || 'all'}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${category === k ? 'bg-[#0F0F10] text-white' : 'text-[#4B4B52] hover:text-[#0F0F10]'}`}>
              {l} <span className="tabular-nums opacity-70 ml-1">{k && list?.counts ? (list.counts[k] ?? 0) : ''}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-4 px-5 mt-4">
        {/* List */}
        <div className="rounded-2xl border border-[var(--hairline)] overflow-hidden">
          <table className="w-full text-sm" data-testid="abuse-user-list">
            <thead className="bg-[var(--surface-2)]">
              <tr className="text-[10px] uppercase tracking-wider text-[#4B4B52]">
                <th className="text-left px-3 py-2">User</th>
                <th className="text-right px-3 py-2">Score</th>
                <th className="text-left px-3 py-2">Category</th>
              </tr>
            </thead>
            <tbody>
              {list?.items?.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-6 text-center italic text-[#8A8A93]">No users have been scored yet.</td></tr>
              ) : (list?.items || []).map((r) => (
                <tr key={r.user_id} onClick={() => openUser(r.user_id)}
                  className={`border-t border-[var(--hairline)] cursor-pointer hover:bg-black/[0.02] ${selected === r.user_id ? 'bg-black/[0.03]' : ''}`}
                  data-testid={`abuse-row-${r.user_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#0F0F10]">{r.user?.name || '—'}</div>
                    <div className="text-[10px] text-[#8A8A93]">{r.user?.email || r.user_id}</div>
                  </td>
                  <td className="px-3 py-2 text-right"><Score v={r.dating_abuse_score} /></td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${CAT_COLOR[r.category] || 'bg-zinc-100 text-zinc-800'}`}>{(r.category || 'normal').toUpperCase()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detail */}
        <div className="rounded-2xl border border-[var(--hairline)] p-4 min-h-[300px]" data-testid="abuse-detail">
          {!selected ? (
            <p className="text-sm text-[#8A8A93] text-center pt-10">Select a user to see the score breakdown.</p>
          ) : !detail ? (
            <div className="flex items-center justify-center pt-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <div>
              <div className="flex items-baseline gap-3 mb-3">
                <div className="font-brand text-2xl font-[700]">{detail.user?.name || detail.user?.email || selected}</div>
                <div className="ml-auto text-3xl font-display font-[700] tabular-nums"><Score v={detail.score?.dating_abuse_score} /></div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${CAT_COLOR[detail.score?.category] || ''}`}>{(detail.score?.category || 'normal').toUpperCase()}</span>
              </div>

              {/* Component breakdown */}
              <div className="mb-4">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B4B52] mb-2">Contribution breakdown</p>
                <ul className="space-y-1" data-testid="abuse-components">
                  {Object.entries(detail.score?.components || {}).sort((a, b) => (b[1]?.score || 0) - (a[1]?.score || 0)).map(([key, val]) => (
                    <li key={key} className="text-xs flex items-center gap-2">
                      <span className="font-mono w-48">{key}</span>
                      <div className="flex-1 h-1.5 bg-[var(--surface-2)] rounded-full overflow-hidden">
                        <div className="h-full bg-[#B91C1C]" style={{ width: `${Math.min(100, (val?.score || 0) * 5)}%` }} />
                      </div>
                      <span className="tabular-nums font-semibold w-10 text-right">{(val?.score || 0).toFixed(1)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Suggested + action buttons */}
              <div className="mb-4 rounded-xl bg-[var(--surface-2)] p-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B4B52] mb-1.5">Suggested action</p>
                <p className="text-sm font-mono mb-2" data-testid="abuse-suggested-action">{detail.suggested?.suggested_action}</p>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => doAction('dismiss')}       disabled={busy} className="text-xs px-2.5 py-1 rounded-full border border-[var(--hairline)] hover:bg-white"><Check size={11} className="inline mr-1" />Dismiss</button>
                  <button onClick={() => doAction('mark_trusted')}  disabled={busy} className="text-xs px-2.5 py-1 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50"><Shield size={11} className="inline mr-1" />Trust</button>
                  <button onClick={() => doAction('rate_limit')}    disabled={busy} className="text-xs px-2.5 py-1 rounded-full border border-amber-200 text-amber-800 hover:bg-amber-50"><AlertTriangle size={11} className="inline mr-1" />Rate limit</button>
                  <button onClick={() => doAction('restrict_first_msgs')} disabled={busy} className="text-xs px-2.5 py-1 rounded-full border border-orange-200 text-orange-800 hover:bg-orange-50"><Pause size={11} className="inline mr-1" />Restrict DMs</button>
                  <button onClick={() => doAction('suspend_pending_review')} disabled={busy} className="text-xs px-2.5 py-1 rounded-full border border-rose-200 text-rose-800 hover:bg-rose-50"><Pause size={11} className="inline mr-1" />Suspend</button>
                  <button onClick={() => doAction('ban')}           disabled={busy} className="text-xs px-2.5 py-1 rounded-full bg-rose-600 text-white hover:bg-rose-700"><Ban size={11} className="inline mr-1" />Ban</button>
                </div>
              </div>

              {/* Recent action log */}
              {detail.recent_actions?.length > 0 && (
                <div className="mb-4">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B4B52] mb-1.5">Recent actions</p>
                  <ul className="text-xs space-y-1" data-testid="abuse-action-log">
                    {detail.recent_actions.map((a, i) => (
                      <li key={i} className="flex gap-2 items-center">
                        <span className="tabular-nums text-[#8A8A93]">{new Date(a.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        <span className="font-mono">{a.action}</span>
                        <span className={`px-1 rounded text-[9px] ${a.mode === 'enforce' ? 'bg-rose-100 text-rose-800' : 'bg-zinc-100 text-zinc-600'}`}>{a.mode}</span>
                        {a.admin_user_id && <span className="text-[#8A8A93]">by {a.admin_user_id}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recent events */}
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B4B52] mb-1.5">Recent events</p>
                <ul className="text-[11px] space-y-0.5 font-mono" data-testid="abuse-recent-events">
                  {(detail.recent_events || []).slice(0, 15).map((e, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-[#8A8A93] tabular-nums shrink-0">{new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                      <span className="text-[#0F0F10]">{e.kind}</span>
                      {e.context?.contact_hits?.length ? <span className="text-rose-700">[{e.context.contact_hits.join(',')}]</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
